import React, { useCallback, useEffect, useRef } from 'react';
import {
  Calculator,
  FileText,
  Loader2,
  NotebookPen,
  Receipt,
  Search,
  User,
  Users,
  X
} from 'lucide-react';
import {
  GlobalSearchGroupDto,
  GlobalSearchItemDto,
  GlobalSearchRecentActivityDto
} from 'api/search';
import GenericBadge from 'components/ui/Badge';
import { GenericColors } from 'enums/genericColors';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useGlobalSearchNavigation } from '../hooks/useGlobalSearchNavigation';

/**
 * Libellés, icônes et couleur par type — le vocabulaire des écrans, pas celui de l'API.
 * Classes Tailwind écrites en dur (jamais composées) pour survivre au purge.
 */
const TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; chip: string }> = {
  client: { label: 'Clients', icon: User, chip: 'bg-blue-100 text-blue-700' },
  contrat: { label: 'Contrats', icon: FileText, chip: 'bg-emerald-100 text-emerald-700' },
  devis: { label: 'Devis', icon: Receipt, chip: 'bg-violet-100 text-violet-700' },
  simulation: { label: 'Simulations', icon: Calculator, chip: 'bg-orange-100 text-orange-700' },
  note: { label: 'Notes', icon: NotebookPen, chip: 'bg-amber-100 text-amber-700' },
  contratGroupe: { label: 'Contrats de groupe', icon: Users, chip: 'bg-teal-100 text-teal-700' }
};

const typeConfig = (type: string) =>
  TYPE_CONFIG[type] ?? { label: type, icon: FileText, chip: 'bg-gray-100 text-gray-600' };

const fmtDate = (date?: Date | null): string =>
  date ? new Date(date).toLocaleDateString('fr-FR') : '';

const toGenericColor = (color?: string | null): GenericColors =>
  (Object.values(GenericColors) as string[]).includes(color ?? '')
    ? (color as GenericColors)
    : GenericColors.Gray;

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Surligne les termes recherchés dans le texte (repérage immédiat du « pourquoi »). */
const Highlight: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const terms = query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length >= 2);
  if (!terms.length) return <>{text}</>;
  const parts = text.split(new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        terms.includes(part.toLowerCase()) ? (
          <mark key={i} className="rounded-sm bg-yellow-100 font-semibold text-inherit">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};

/** Sous-ligne « dernière simulation » d'un client (contexte enrichi). */
const RecentActivityRow: React.FC<{
  activity: GlobalSearchRecentActivityDto;
  clickable: boolean;
  onOpen: (activity: GlobalSearchRecentActivityDto) => void;
}> = ({ activity, clickable, onOpen }) => (
  <button
    type="button"
    disabled={!clickable}
    onClick={() => onOpen(activity)}
    className={`flex w-full items-center gap-2 py-1.5 pl-3 pr-2 text-left text-xs ${
      clickable ? 'text-gray-600 hover:bg-white hover:text-primary' : 'text-gray-400'
    }`}
  >
    <Calculator className="h-3.5 w-3.5 shrink-0 text-orange-400" />
    <span className="font-semibold">{activity.title}</span>
    {activity.branchCode && (
      <span className="rounded bg-white px-1.5 py-px text-[10px] font-medium text-gray-500 ring-1 ring-gray-200">
        {activity.branchCode}
      </span>
    )}
    {activity.statusLabel && <span className="text-gray-400">{activity.statusLabel}</span>}
    <span className="ml-auto shrink-0 tabular-nums text-gray-400">{fmtDate(activity.date)}</span>
  </button>
);

/** Une ligne de résultat (forme commune à tous les types). */
const ResultRow: React.FC<{
  item: GlobalSearchItemDto;
  query: string;
  onOpen: (item: GlobalSearchItemDto) => void;
  onOpenActivity: (activity: GlobalSearchRecentActivityDto) => void;
  canOpenActivity: boolean;
}> = ({ item, query, onOpen, onOpenActivity, canOpenActivity }) => {
  const { icon: Icon, chip } = typeConfig(item.type);
  const activities = item.context?.recentActivities ?? [];
  return (
  <div className="px-2 py-1">
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-primary/5"
    >
      {/* Pastille type : repérage visuel immédiat du type de résultat */}
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${chip}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-gray-900">
            <Highlight text={item.title} query={query} />
          </span>
          {item.badge && (
            <GenericBadge
              label={item.badge}
              color={toGenericColor(item.badgeColor)}
              badgeType="sm-bold"
            />
          )}
        </div>
        {item.subtitle && (
          <div className="mt-0.5 truncate text-xs text-gray-500">
            <Highlight text={item.subtitle} query={query} />
          </div>
        )}
        {/* Compteurs du contexte client (3 premiers clients uniquement) */}
        {item.context && (
          <div className="mt-1.5 flex gap-2">
            {item.context.activeContractsCount !== undefined && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-100">
                {item.context.activeContractsCount} contrat{item.context.activeContractsCount > 1 ? 's' : ''} actif{item.context.activeContractsCount > 1 ? 's' : ''}
              </span>
            )}
            {item.context.quotesCount !== undefined && (
              <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-100">
                {item.context.quotesCount} devis
              </span>
            )}
            {item.context.simulationsCount !== undefined && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-100">
                {item.context.simulationsCount} simulation{item.context.simulationsCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>
      {item.date && (
        <span className="shrink-0 tabular-nums text-xs text-gray-400">{fmtDate(item.date)}</span>
      )}
    </button>
    {/* Dernières simulations du client : bloc indenté, encadré, sous la ligne */}
    {activities.length > 0 && (
      <div className="ml-[52px] mt-0.5 overflow-hidden rounded-lg border border-gray-100 bg-gray-50/70">
        {activities.map((activity) => (
          <RecentActivityRow
            key={activity.id}
            activity={activity}
            clickable={canOpenActivity}
            onOpen={onOpenActivity}
          />
        ))}
      </div>
    )}
  </div>
  );
};

/** Un groupe (Clients, Contrats…) : en-tête coloré collant + lignes + « voir plus ». */
const ResultGroup: React.FC<{
  group: GlobalSearchGroupDto;
  query: string;
  isExpanded: boolean;
  onExpand: (type: string) => void;
  onOpen: (item: GlobalSearchItemDto) => void;
  onOpenActivity: (activity: GlobalSearchRecentActivityDto) => void;
  canOpenActivity: boolean;
}> = ({ group, query, isExpanded, onExpand, onOpen, onOpenActivity, canOpenActivity }) => {
  const { label, icon: Icon, chip } = typeConfig(group.type);
  const hasMore = !isExpanded && group.totalCount > group.items.length;

  return (
    <section>
      {/* Bandeau de section bien marqué, collant au scroll */}
      <div className="sticky top-0 z-10 flex items-center gap-2.5 border-y border-gray-200/70 bg-gray-100 px-4 py-2.5">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${chip}`}>
          <Icon className="h-4 w-4" />
        </span>
        <h4 className="text-sm font-bold text-gray-800">{label}</h4>
        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold tabular-nums text-gray-600 shadow-sm ring-1 ring-gray-200">
          {group.totalCount}
        </span>
      </div>
      <div className="divide-y divide-gray-50 py-1">
        {group.items.map((item) => (
          <ResultRow
            key={`${item.type}:${item.id}`}
            item={item}
            query={query}
            onOpen={onOpen}
            onOpenActivity={onOpenActivity}
            canOpenActivity={canOpenActivity}
          />
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={() => onExpand(group.type)}
            className="mx-2 mb-1 w-[calc(100%-16px)] rounded-lg bg-primary/5 px-3 py-2 text-left text-xs font-bold text-primary transition-colors hover:bg-primary/10"
          >
            Voir les {group.totalCount} {label.toLowerCase()} →
          </button>
        )}
      </div>
    </section>
  );
};

/**
 * Barre de recherche globale du header : clients, contrats, devis, simulations, notes,
 * contrats de groupe. Le back scope selon le rôle actif — rien à gérer côté front.
 */
export const GlobalSearchBar: React.FC<{ className?: string }> = ({ className }) => {
  const {
    searchText,
    setSearchText,
    groups,
    isLoading,
    hasSearched,
    isActive,
    expandedTypes,
    expandGroup,
    reset
  } = useGlobalSearch();
  const { navigateToItem, navigateToActivity, canOpenSimulation } =
    useGlobalSearchNavigation(reset);

  const containerRef = useRef<HTMLDivElement>(null);

  // Fermeture au clic extérieur / Échap
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        reset();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') reset();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [reset]);

  const onOpenItem = useCallback(
    (item: Parameters<typeof navigateToItem>[0]) => {
      void navigateToItem(item);
    },
    [navigateToItem]
  );

  const onOpenActivity = useCallback(
    (activity: Parameters<typeof navigateToActivity>[0]) => {
      void navigateToActivity(activity);
    },
    [navigateToActivity]
  );

  const showDropdown = isActive && (isLoading || hasSearched);

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      {/* Champ de recherche */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Rechercher un client, un contrat, un devis, une simulation…"
          autoComplete="off"
          className="w-full rounded-full border bg-white py-3 pl-10 pr-10 text-sm leading-5 text-gray-900 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-25"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : (
            searchText && (
              <button type="button" onClick={reset} aria-label="Effacer la recherche">
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Résultats */}
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-2xl">
          {groups.length > 0 ? (
            groups.map((group) => (
              <ResultGroup
                key={group.type}
                group={group}
                query={searchText}
                isExpanded={expandedTypes.has(group.type)}
                onExpand={expandGroup}
                onOpen={onOpenItem}
                onOpenActivity={onOpenActivity}
                canOpenActivity={canOpenSimulation}
              />
            ))
          ) : isLoading ? (
            <div className="flex items-center gap-2 px-5 py-4 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Recherche en cours…
            </div>
          ) : (
            <div className="px-5 py-4 text-sm text-gray-500">
              Aucun résultat pour « {searchText.trim()} ». Essayez un nom, un email, un
              n° de contrat ou de dossier.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
